import { sql } from 'drizzle-orm';
import { text, integer, sqliteTable, uniqueIndex, index, type AnySQLiteColumn } from 'drizzle-orm/sqlite-core';

// Book status enum values
export const bookStatuses = [
  'ongoing',
  'completed',
  'hiatus',
  'canceled',
  'upcoming',
  'pilot',
  'season_break',
] as const;

export type BookStatus = (typeof bookStatuses)[number];

// Book type enum values
export const bookTypes = ['webtoon', 'manga', 'novel'] as const;

export type BookType = (typeof bookTypes)[number];

// Books table
export const books = sqliteTable(
  'books',
  {
    id: text('id')
      .primaryKey()
      .$defaultFn(() => crypto.randomUUID()),
    externalId: text('external_id'),
    source: text('source'),
    title: text('title').notNull(),
    slug: text('slug'),
    author: text('author'),
    locale: text('locale'),
    premise: text('premise'),
    status: text('status').$type<BookStatus>(),
    type: text('type').$type<BookType>(),
    // JSON stored as text in SQLite
    config: text('config', { mode: 'json' }).$type<Record<string, unknown>>(),
    publishedAt: integer('published_at', { mode: 'timestamp' }),
    insertedAt: integer('inserted_at', { mode: 'timestamp' })
      .notNull()
      .default(sql`(unixepoch())`),
    updatedAt: integer('updated_at', { mode: 'timestamp' })
      .notNull()
      .default(sql`(unixepoch())`),
    // Self-referential foreign key for original book
    originalBookId: text('original_book_id').references((): AnySQLiteColumn => books.id),
  },
  (table) => ({
    // Unique index on source and external_id
    uniqueSourceIdx: uniqueIndex('books_unique_source_index').on(
      table.source,
      table.externalId
    ),
    // Index on slug for fast lookup
    slugIdx: index('books_slug_index').on(table.slug),
  })
);

// Book chapters table
export const bookChapters = sqliteTable(
  'book_chapters',
  {
    id: text('id')
      .primaryKey()
      .$defaultFn(() => crypto.randomUUID()),
    externalId: text('external_id'),
    title: text('title'),
    slug: text('slug'),
    number: text('number'),
    // JSON stored as text in SQLite
    content: text('content', { mode: 'json' }).$type<Record<string, unknown>>(),
    publishedAt: integer('published_at', { mode: 'timestamp' }),
    insertedAt: integer('inserted_at', { mode: 'timestamp' })
      .notNull()
      .default(sql`(unixepoch())`),
    updatedAt: integer('updated_at', { mode: 'timestamp' })
      .notNull()
      .default(sql`(unixepoch())`),
    // Foreign key to books table
    bookId: text('book_id')
      .notNull()
      .references(() => books.id),
    // Engagement metrics
    views: integer('views'),
    reach: integer('reach'),
    reactionCount: integer('reaction_count'),
    commentCount: integer('comment_count'),
    shareCount: integer('share_count'),
  },
  (table) => ({
    // Unique index on book_id and external_id
    uniqueSourceIdx: uniqueIndex('book_chapters_unique_source_index').on(
      table.bookId,
      table.externalId
    ),
    // Index on book_id and published_at for sorting
    bookPublishedIdx: index('book_chapters_book_published_index').on(
      table.bookId,
      table.publishedAt
    ),
    // Index on slug for fast lookup
    slugIdx: index('book_chapters_slug_index').on(table.slug),
    // Index for feed ordering (views + published_at)
    feedIdx: index('book_chapters_feed_index').on(
      table.views,
      table.publishedAt
    ),
    // Index on external_id for engagement webhook lookup
    externalIdIdx: index('book_chapters_external_id_index').on(table.externalId),
  })
);

// Type exports for use in application code
export type Book = typeof books.$inferSelect;
export type NewBook = typeof books.$inferInsert;
export type BookChapter = typeof bookChapters.$inferSelect;
export type NewBookChapter = typeof bookChapters.$inferInsert;
